import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  useClipboardAgentSearchQuery,
  useClipboardTreeQuery,
} from "@/features/clipboard/clipboardQueries";
import { useClipboardSelection } from "@/features/clipboard/context/ClipboardSelectionContext";
import { useClipboardRoute } from "@/features/clipboard/hooks/useClipboardRoute";
import {
  clipboardDocumentUrl,
  clipboardFolderUrl,
  clipboardSearchUrl,
} from "@/features/clipboard/utils/urls";
import { formatDuration } from "@/utils/format";
import { useNavigate } from "react-router-dom";

const Sep = () => (
  <span aria-hidden className="mx-1 text-zinc-300">
    /
  </span>
);

export const ClipboardNavBar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    path,
    routeDocumentId,
    routeDocumentSubpath,
    routeSearchId,
    sharedOwnerId,
  } = useClipboardRoute();
  const { inspectedDoc } = useTreeViewOutletContext();
  const { clearSelection } = useClipboardSelection();
  const { data: tree } = useClipboardTreeQuery(path, sharedOwnerId, !!user);
  const { data: activeSearch } = useClipboardAgentSearchQuery(routeSearchId, !!user);

  const crumbs = path ? path.split("/") : [];
  const sharedByEmail =
    tree?.folders.find((f) => f.shared && f.owner_id === sharedOwnerId)?.owner_email ??
    null;

  return (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <nav className="flex flex-wrap items-baseline text-base leading-6" aria-label="Clipboard location">
          <button
            type="button"
            onClick={() => {
              navigate("/clipboard");
              clearSelection();
            }}
            className={`transition-colors hover:text-zinc-900 ${path || routeSearchId || routeDocumentId ? "text-zinc-400" : "text-zinc-900"}`}
          >
            Clipboard
          </button>
          {crumbs.map((c, i) => {
            const target = crumbs.slice(0, i + 1).join("/");
            const isLast = i === crumbs.length - 1;
            return (
              <span key={target} className="flex items-baseline">
                <Sep />
                <button
                  type="button"
                  onClick={() => {
                    navigate(clipboardFolderUrl(target, sharedOwnerId));
                    clearSelection();
                  }}
                  className={`transition-colors hover:text-zinc-900 ${isLast ? "text-zinc-900" : "text-zinc-400"}`}
                >
                  {c}
                </button>
              </span>
            );
          })}
          {routeSearchId && activeSearch && (
            <span className="flex min-w-0 items-baseline">
              <Sep />
              <button
                type="button"
                onClick={() => {
                  navigate(clipboardSearchUrl(routeSearchId));
                  clearSelection();
                }}
                className={`max-w-md truncate italic transition-colors hover:text-zinc-900 ${routeDocumentId ? "text-zinc-400" : "text-zinc-900"
                  }`}
              >
                {activeSearch.query}
              </button>
            </span>
          )}
          {routeDocumentId && (
            <span className="flex min-w-0 items-baseline">
              <Sep />
              <button
                type="button"
                onClick={() =>
                  navigate(
                    clipboardDocumentUrl(
                      routeDocumentId,
                      path,
                      sharedOwnerId,
                      routeSearchId,
                    ),
                  )
                }
                title="This is a file — its parsed contents are shown as a folder"
                className={`flex min-w-0 items-baseline hover:text-zinc-900 ${routeDocumentSubpath
                  ? "text-zinc-400"
                  : "text-zinc-900"
                  }`}
              >
                <span className="max-w-xs truncate">
                  {inspectedDoc?.filename || inspectedDoc?.title || "Document"}
                </span>
              </button>
            </span>
          )}
          {routeDocumentSubpath.split("/").filter(Boolean).map((segment, index, parts) => {
            const target = parts.slice(0, index + 1).join("/");
            const isLast = index === parts.length - 1;
            return (
              <span key={target} className="flex min-w-0 items-baseline">
                <Sep />
                <button
                  type="button"
                  onClick={() => {
                    if (!routeDocumentId) return;
                    navigate(
                      clipboardDocumentUrl(
                        routeDocumentId,
                        path,
                        sharedOwnerId,
                        routeSearchId,
                        target,
                      ),
                    );
                  }}
                  className={`max-w-xs truncate font-mono text-sm hover:text-zinc-900 ${isLast ? "text-zinc-900" : "text-zinc-400"
                    }`}
                >
                  {segment}
                </button>
              </span>
            );
          })}
        </nav>

        {sharedOwnerId && (
          <span className="text-sm italic text-zinc-400">
            Shared by {sharedByEmail || "another user"}
          </span>
        )}
      </div>

      {routeSearchId &&
        activeSearch &&
        (() => {
          const inFlight = ["pending", "dispatched", "running", "executing", "retry"].includes(
            activeSearch.status,
          );
          // Before a command is chosen we're still planning; say so instead of
          // leaving an empty pause.
          if (!activeSearch.planned_command) {
            if (!inFlight) return null;
            return (
              <p className="mt-2 text-sm italic text-zinc-400">Planning…</p>
            );
          }
          return (
            <div className="mt-2 space-y-1 text-sm text-zinc-400">
              <div className="flex min-w-0 items-center gap-2">
                <span className="w-20 shrink-0 text-2xs uppercase tracking-[0.18em]">Command</span>
                <code
                  className="truncate font-mono text-xs text-zinc-600"
                  title={activeSearch.planned_command}
                >
                  {activeSearch.planned_command}
                </code>
                {inFlight && <span className="ml-auto shrink-0 italic text-zinc-400">Running…</span>}
              </div>
              {activeSearch.execution_query && (
                <div className="flex min-w-0 items-center gap-2">
                  <span className="w-20 shrink-0 text-2xs uppercase tracking-[0.18em]">
                    {activeSearch.operation === "grep"
                      ? "Regex"
                      : activeSearch.operation === "ls"
                        ? "Path"
                        : "Query"}
                  </span>
                  <code
                    className="truncate font-mono text-xs text-zinc-600"
                    title={activeSearch.execution_query}
                  >
                    {activeSearch.execution_query}
                  </code>
                  {activeSearch.execution_ms != null && (
                    <span className="ml-auto shrink-0 tabular-nums text-zinc-400">
                      {formatDuration(activeSearch.execution_ms)}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })()}
    </>
  );
};
